import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { UtensilsCrossed } from 'lucide-react';
import { MenuItem, Category, AlcoholSubCategory } from '../types';
import { LunchMenuModule } from './LunchMenu';
import { supabase } from '../lib/supabase';
import { useSettings } from '../context/SettingsContext';
import { getOptimizedImageUrl } from '../utils';

export function MenuSection() {
  const { settings, loading: settingsLoading } = useSettings();
  const sectionRef = useRef<HTMLElement>(null);
  const [shouldFetch, setShouldFetch] = useState(false);
  
  const categories = useMemo(() => {
    return settings.menu_categories 
      ? settings.menu_categories.split(',').map(s => s.trim() as Category).filter(Boolean) 
      : [];
  }, [settings.menu_categories]);

  const alcoholSubCategories = useMemo(() => {
    return settings.menu_subcategories 
      ? settings.menu_subcategories.split(',').map(s => s.trim() as AlcoholSubCategory).filter(Boolean) 
      : [];
  }, [settings.menu_subcategories]);

  const [activeCategory, setActiveCategory] = useState<Category>('Пица');
  const [activeSubCategory, setActiveSubCategory] = useState<AlcoholSubCategory | null>(null);
  const [dbItems, setDbItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasInitialScrolled, setHasInitialScrolled] = useState(false);

  // Dynamically and reactively sort menu items based on menu_items_order from latest settings
  const sortedDbItems = useMemo(() => {
    if (!dbItems || dbItems.length === 0) return [];
    const itemsCopy = [...dbItems];
    if (settings.menu_items_order) {
      const orderArr = settings.menu_items_order.split(',');
      itemsCopy.sort((a, b) => {
        let idxA = orderArr.indexOf(a.id);
        let idxB = orderArr.indexOf(b.id);
        if (idxA === -1) idxA = 999999;
        if (idxB === -1) idxB = 999999;
        if (idxA === idxB) return a.title.localeCompare(b.title);
        return idxA - idxB;
      });
    } else {
      itemsCopy.sort((a, b) => a.title.localeCompare(b.title));
    }
    return itemsCopy;
  }, [dbItems, settings.menu_items_order]);

  // Set default category when categories load or sortedDbItems changes
  useEffect(() => {
    if (categories.length > 0 && !loading) {
      const activeHasItems = sortedDbItems.some(i => i.category === activeCategory);
      if (!activeHasItems) {
        const firstWithItems = categories.find(c => sortedDbItems.some(i => i.category === c));
        if (firstWithItems) {
          setActiveCategory(firstWithItems);
        }
      }
    }
  }, [categories, activeCategory, sortedDbItems, loading]);

  // Defer fetching until the menu comes into view
  useEffect(() => {
    // If it's already fetching or we have a cached version that is valid, we can fetch
    if (sessionStorage.getItem('menu_cache')) {
      setShouldFetch(true);
      return;
    }

    const currentRef = sectionRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setShouldFetch(true);
        observer.disconnect();
      }
    }, { rootMargin: '300px' });

    observer.observe(currentRef);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldFetch) return;

    async function fetchMenu() {
      try {
        const cached = sessionStorage.getItem('menu_cache');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setDbItems(parsed);
              setLoading(false);
            }
          } catch (e) {}
        }

        const { data, error } = await supabase
          .from('menu_items')
          .select('*')
          .eq('is_active', true);

        if (!error && data) {
          setDbItems(data);
          sessionStorage.setItem('menu_cache', JSON.stringify(data));
        } else if (!cached) {
          setDbItems([]);
        }
      } catch (err) {
        if (!sessionStorage.getItem('menu_cache')) {
          setDbItems([]);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchMenu();
  }, [shouldFetch]);

  useEffect(() => {
    if (activeCategory === 'Алкохолни напитки') {
      setActiveSubCategory('Наливна бира');
    } else {
      setActiveSubCategory(null);
    }
  }, [activeCategory]);

  const scrollToTab = (el: HTMLElement | null) => {
    if (!el || !el.parentElement) return;
    const container = el.parentElement;
    const scrollLeft = container.scrollLeft + (el.getBoundingClientRect().left - container.getBoundingClientRect().left) - (container.clientWidth / 2) + (el.clientWidth / 2);
    container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  };

  // Initial scroll to active tab on mobile so it's not hidden
  useEffect(() => {
    if (!hasInitialScrolled && categories.length > 0 && !loading) {
      const timer = setTimeout(() => {
        // Find by id (handle edge case if space exists in category name safely)
        const id = `category-${activeCategory}`;
        const el = document.getElementById(id);
        if (el) {
          scrollToTab(el);
          setHasInitialScrolled(true);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [hasInitialScrolled, categories.length, loading, activeCategory]);

  // Preload all valid images in background so they don't flash on category switch
  useEffect(() => {
    if (sortedDbItems.length > 0) {
      sortedDbItems.forEach(item => {
        if (item.image && typeof item.image === 'string' && item.image.startsWith('http')) {
          const img = new window.Image();
          img.src = item.image;
        }
      });
    }
  }, [sortedDbItems]);

  const filteredItems = useMemo(() => {
    let items = sortedDbItems.filter(item => item.category === activeCategory);
    if (activeCategory === 'Алкохолни напитки' && activeSubCategory) {
      items = items.filter(item => item.subCategory === activeSubCategory || (item as any).sub_category === activeSubCategory);
    }
    return items;
  }, [activeCategory, activeSubCategory, sortedDbItems]);


  return (
    <section id="menu" ref={sectionRef} className="py-16 md:py-32 bg-gusto-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mb-10 md:mb-20 text-center md:text-left"
        >
          <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl text-gusto-dark tracking-tight mb-3 md:mb-4">
            {settings.menu_label}
          </h2>
          <p className="font-serif italic text-xl sm:text-2xl md:text-3xl text-gray-600">
            {settings.menu_title}
          </p>
        </motion.div>

        {/* Categories Tabs */}
        <div className="flex overflow-x-auto md:flex-wrap md:justify-center pb-4 mb-4 md:mb-12 gap-2 sm:gap-3 -mx-4 px-4 md:mx-0 md:px-2 hide-scrollbar scroll-smooth">
          {categories.map((category) => (
            <button
              id={`category-${category}`}
              key={category}
              onClick={(e) => {
                setActiveCategory(category);
                scrollToTab(e.currentTarget);
              }}
              className={`whitespace-nowrap flex-shrink-0 px-4 py-2 sm:px-6 sm:py-2.5 rounded-full font-medium text-xs sm:text-sm md:text-base border transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-gusto-dark text-white border-gusto-dark shadow-md'
                  : 'bg-transparent text-gusto-dark border-gusto-gray/30 hover:border-gusto-dark hover:bg-gusto-dark/5'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Subcategories Tabs */}
        {activeCategory === 'Алкохолни напитки' && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }} 
            animate={{ opacity: 1, height: 'auto' }} 
            className="flex overflow-x-auto md:flex-wrap md:justify-center pb-8 mb-4 gap-2 sm:gap-3 -mx-4 px-4 md:mx-0 md:px-2 hide-scrollbar scroll-smooth"
          >
            {alcoholSubCategories.map((subCat) => (
              <button
                key={subCat}
                onClick={(e) => {
                  setActiveSubCategory(subCat);
                  scrollToTab(e.currentTarget);
                }}
                className={`whitespace-nowrap flex-shrink-0 px-4 py-1.5 sm:px-5 sm:py-2 rounded-full font-medium text-xs sm:text-sm border transition-all duration-300 ${
                  activeSubCategory === subCat
                    ? 'bg-gusto-gold text-white border-gusto-gold shadow-md'
                    : 'bg-transparent text-gusto-dark border-gusto-gray/30 hover:border-gusto-gold hover:text-gusto-gold hover:bg-gusto-gold/5'
                }`}
              >
                {subCat}
              </button>
            ))}
          </motion.div>
        )}

        {/* Grid or Module */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8"
            >
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm flex flex-col border border-gusto-gray/10 sm:h-[380px] h-[280px] animate-pulse">
                  <div className="h-[120px] sm:h-56 bg-gray-200"></div>
                  <div className="p-3 sm:p-4 flex-grow flex flex-col sm:py-5 sm:px-5">
                    <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="space-y-2">
                       <div className="h-3 bg-gray-200 rounded w-full"></div>
                       <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    </div>
                    <div className="mt-auto pt-4 flex gap-4 items-end justify-between">
                       <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                       <div className="h-6 bg-gray-200 rounded w-1/6"></div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          ) : activeCategory === 'Обедно меню' ? (
            <motion.div
              key="lunch-menu"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4 }}
              className="w-full"
            >
              <LunchMenuModule />
            </motion.div>
          ) : (
            <motion.div
              key="regular-menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8"
            >
              {sortedDbItems.map((item) => {
                const isCatMatch = item.category === activeCategory;
                const subCatValue = (item as any).sub_category || item.subCategory;
                const isSubMatch = activeCategory !== 'Алкохолни напитки' || subCatValue === activeSubCategory;
                const isActive = isCatMatch && isSubMatch;
                
                return (
                  <div key={item.id} style={{ display: isActive ? 'block' : 'none' }}>
                    <MenuCard item={item} />
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </section>
  );
}

function MenuCard({ item }: { item: MenuItem, key?: React.Key }) {
  return (
    <div
      className="group bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col border border-gusto-gray/10 h-full"
      style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.02)' }}
    >
      <div className="relative h-28 sm:h-48 md:h-56 overflow-hidden shrink-0 bg-[#f7f5f0]">
        {item.image ? (
          <img
            src={getOptimizedImageUrl(item.image, 600)}
            alt={item.title}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              if (e.target && (e.target as HTMLElement).nextElementSibling) {
                ((e.target as HTMLElement).nextElementSibling as HTMLElement).style.display = 'flex';
              }
            }}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 relative z-10"
          />
        ) : null}
        
        {/* Fallback pattern when no image or image fails to load */}
        <div 
          className={`absolute inset-0 flex items-center justify-center ${item.image ? 'hidden' : 'flex'}`}
        >
          <UtensilsCrossed className="w-10 h-10 sm:w-16 sm:h-16 text-gusto-gray/10" strokeWidth={1} />
        </div>
        
        {/* Badges */}
        <div className="absolute top-2 left-2 sm:top-4 sm:left-4 flex flex-col gap-1 sm:gap-2">
          {item.badges?.map(badge => (
            <span
              key={badge}
              className={`text-[9px] sm:text-xs font-medium px-2 py-0.5 sm:px-3 sm:py-1 rounded-full shadow-sm ${
                badge === 'Вегетарианска' ? 'bg-gusto-green text-white' : 
                badge === 'Люта' ? 'bg-gusto-tomato text-white' : 
                'bg-white text-gusto-dark'
              }`}
            >
              {badge}
            </span>
          ))}
        </div>
      </div>
      
      <div className="p-3 sm:p-5 md:p-6 flex flex-col flex-1">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-4 mb-1 sm:mb-2 text-center sm:text-left">
          <h3 className="font-heading font-semibold text-sm sm:text-lg md:text-xl text-gusto-dark leading-tight line-clamp-2 sm:line-clamp-none">
            {item.title}
          </h3>
          <span className="font-heading font-bold whitespace-nowrap">
            <span className="text-gusto-red text-sm sm:text-base md:text-lg">{item.price.toFixed(2)} €</span>
          </span>
        </div>
        
        <p className="text-gray-600 text-[10px] sm:text-sm mb-3 sm:mb-6 flex-1 leading-relaxed line-clamp-3 sm:line-clamp-none text-center sm:text-left">
          {item.description}
        </p>
        
        {item.weight && (
          <div className="flex items-center justify-center sm:justify-between text-[10px] sm:text-xs text-gray-500 pt-2 sm:pt-4 border-t border-gray-100 mt-auto">
            <span className="font-medium text-gray-600">{item.weight}</span>
          </div>
        )}
      </div>
    </div>
  );
}
